import React, { useState, useEffect } from "react";
import axios from "axios";
import { Search, Wallet, CheckCircle, XCircle } from "lucide-react";

export default function FundsAdmin() {
  const [funds, setFunds] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);

  // ✅ Fetch All Funds from Backend
  const fetchFunds = async () => {
    try {
      setLoading(true);
      const res = await axios.get("https://api.speedfollowinfo.com/api/admin/funds");
      const data = Array.isArray(res.data.funds)
        ? res.data.funds
        : res.data || [];
      setFunds(data);
      setLoading(false);
    } catch (err) {
      console.error("❌ Fetch Funds Error:", err.response?.data || err.message);
      setFunds([]);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFunds();
  }, []);

  // ✅ Approve or Reject Fund
  const handleFundAction = async (id, status) => {
    try {
      const res = await axios.put(
        `https://api.speedfollowinfo.com/api/admin/fund-status/${id}`,
        { status }
      );
      alert(res.data.message);
      fetchFunds(); // refresh list
    } catch (err) {
      console.error("❌ Fund Action Error:", err.response?.data || err.message);
      alert("Something went wrong!");
    }
  };

  // ✅ Filter by username or method
  const filteredFunds = funds.filter(
    (f) =>
      f.userId?.username?.toLowerCase().includes(search.toLowerCase()) ||
      f.method.toLowerCase().includes(search.toLowerCase()) ||
      f.status.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-[#0b0f1a] text-white p-6 md:p-10">
      <h1 className="text-2xl font-bold mb-6">💰 Fund Requests (Admin Panel)</h1>

      {/* 🔍 Search Bar */}
      <div className="flex items-center gap-3 bg-[#111827] px-4 py-2 rounded-lg mb-4">
        <Search size={20} className="text-gray-400" />
        <input
          type="text"
          placeholder="Search by username, method, or status..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="bg-transparent outline-none w-full text-white"
        />
      </div>

      {/* 🧾 Table */}
      <div className="overflow-x-auto">
        <table className="w-full text-left border-collapse">
          <thead>
            <tr className="bg-[#111827] text-[#f2a900]">
              <th className="p-3 border-b border-gray-700">#</th>
              <th className="p-3 border-b border-gray-700">User</th>
              <th className="p-3 border-b border-gray-700">Method</th>
              <th className="p-3 border-b border-gray-700">Amount</th>
              <th className="p-3 border-b border-gray-700">Status</th>
              <th className="p-3 border-b border-gray-700">Transaction ID</th>
              <th className="p-3 border-b border-gray-700 text-center">Action</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="7" className="text-center py-6 text-gray-400">
                  Loading funds...
                </td>
              </tr>
            ) : filteredFunds.length === 0 ? (
              <tr>
                <td colSpan="7" className="text-center py-6 text-gray-400 italic">
                  No fund requests found.
                </td>
              </tr>
            ) : (
              filteredFunds.map((fund, index) => (
                <tr
                  key={fund._id}
                  className="hover:bg-[#1b2138] transition border-b border-gray-800"
                >
                  <td className="p-3">{index + 1}</td>
                  <td className="p-3 font-semibold">
                    {fund.userId?.username || "Unknown"}
                  </td>
                  <td className="p-3">{fund.method}</td>
                  <td className="p-3 text-[#ffd700] font-semibold">
                    ৳{fund.amount.toFixed(2)}
                  </td>
                  <td
                    className={`p-3 font-semibold ${
                      fund.status === "Approved"
                        ? "text-green-400"
                        : fund.status === "Pending"
                        ? "text-yellow-400"
                        : "text-red-400"
                    }`}
                  >
                    {fund.status}
                  </td>
                  <td className="p-3 text-gray-400 text-sm">
                    {fund.transactionId || "N/A"}
                  </td>
                  <td className="p-3 flex items-center justify-center gap-3">
                    {fund.status === "Pending" ? (
                      <>
                        <button
                          onClick={() => handleFundAction(fund._id, "Approved")}
                          className="bg-green-600 hover:bg-green-700 px-3 py-1 rounded-md flex items-center gap-1"
                        >
                          <CheckCircle size={16} /> Approve
                        </button>
                        <button
                          onClick={() => handleFundAction(fund._id, "Rejected")}
                          className="bg-red-600 hover:bg-red-700 px-3 py-1 rounded-md flex items-center gap-1"
                        >
                          <XCircle size={16} /> Reject
                        </button>
                      </>
                    ) : (
                      <span className="text-gray-500">—</span>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}